import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { FaEnvelope, FaFacebook, FaInstagram } from 'react-icons/fa';

/** Toast usando layout de daisyUI (toast + alert) */
const Toast = ({ type = 'success', message = '', onClose }) => {
  if (!message) return null;
  const tone = type === 'success' ? 'alert-success' : 'alert-error';
  return (
    <div className="toast toast-end z-50">
      <div className={`alert ${tone} shadow-lg`}>
        <span className="text-sm">{message}</span>
        <button
          type="button"
          onClick={onClose}
          className="btn btn-ghost btn-xs"
          aria-label="Cerrar notificación"
        >
          ✖
        </button>
      </div>
    </div>
  );
};

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    apellido: '',
    number: '',
    hotel: '',
    message: '',
    sendto: ''
  });
  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [responseType, setResponseType] = useState('success'); // 'success' | 'error'

  // Auto-hide del toast
  useEffect(() => {
    if (!responseMessage) return;
    const t = setTimeout(() => setResponseMessage(''), 4500);
    return () => clearTimeout(t);
  }, [responseMessage]);

  // Opciones de hoteles (sin cambios)
  const hotelOptions = [
    { value: '', label: 'Seleccione Hotel' },
    { value: 'Hotel Lyon', label: 'Hotel Lyon' },
    { value: 'Hotel San Miguel Plaza Spa', label: 'Hotel San Miguel Plaza Spa' },
    { value: 'Posada Sierras de Córdoba', label: 'Posada Sierras de Córdoba' },
    { value: 'Hotel Kalken', label: 'Hotel Kalken' },
    { value: 'Hotel Villa de Merlo', label: 'Hotel Villa de Merlo' },
    { value: 'Hotel Azul', label: 'Hotel Azul' },
    { value: 'Grand Hotel', label: 'Grand Hotel' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    // mismo manejo; limpieza visual suave en number
    setFormData({
      ...formData,
      [name]: name === 'number' ? value.replace(/[^\d +()-]/g, '') : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResponseMessage('');

    const homologoFormData = {
      user: {
        nombre: formData.name,
        apellido: formData.apellido,
        phone: formData.number,
        mail: formData.sendto,
      },
      hotel: formData.hotel,
      body: formData.message,
      subject: 'Arrepentimiento de compra'
    };

    try {
      const response = await axios.post('https://api.magnahoteles.com/api/public/contact', homologoFormData);
      if (response?.data) {
        setResponseType('success');
        setResponseMessage('Mensaje enviado con éxito. Nos pondremos en contacto pronto.');
        setFormData({
          name: '',
          apellido: '',
          number: '',
          hotel: '',
          message: '',
          sendto: ''
        });
      } else {
        setResponseType('error');
        setResponseMessage('No pudimos confirmar el envío. Probá nuevamente.');
      }
    } catch (error) {
      setResponseType('error');
      setResponseMessage('Hubo un error al enviar el mensaje. Por favor, intentá más tarde.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Toast */}
      <Toast
        type={responseType}
        message={responseMessage}
        onClose={() => setResponseMessage('')}
      />

      <div className="my-6 px-4">
        <div className="max-w-5xl mx-auto grid sm:grid-cols-2 gap-8">
          {/* Columna izquierda: info en card glass */}
          <div className="card glass shadow-xl">
            <div className="card-body">
              <h1 className="card-title text-3xl font-extrabold text-red-500">Botón de arrepentimiento</h1>
              <p className="text-sm leading-relaxed">
                Mediante este formulario podrás solicitar la cancelación sin penalidad de tu reserva dentro del plazo de hasta
                <strong> 10 días corridos</strong> desde la confirmación y hasta <strong>24 h</strong> antes del horario de ingreso.
                Completá tus datos para procesar la solicitud y enviarte la cancelación formal a la brevedad.
              </p>

              <div className="divider my-2"></div>

              <div className="space-y-3">
                <h2 className="text-lg font-bold">Email</h2>
                <div className="flex items-center gap-3">
                  <div className="bg-base-200 h-10 w-10 rounded-full grid place-items-center">
                    <FaEnvelope className="opacity-70" />
                  </div>
                  <strong className="select-all">reservas1@magnahoteles.com</strong>
                </div>
              </div>

              <div className="space-y-3 mt-6">
                <h2 className="text-lg font-bold">Redes</h2>
                <div className="flex gap-3">
                  <a className="btn btn-circle btn-ghost hover:bg-base-200" href="https://www.facebook.com/magnahotelesok" aria-label="Facebook Magna Hoteles">
                    <FaFacebook />
                  </a>
                  <a className="btn btn-circle btn-ghost hover:bg-base-200" href="https://www.instagram.com/magnahoteles/" aria-label="Instagram Magna Hoteles">
                    <FaInstagram />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Columna derecha: formulario en card glass */}
          <div className="card glass shadow-xl">
            <div className="card-body">
              <form onSubmit={handleSubmit} noValidate className="space-y-4">
                {/* fila 1 */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="form-control">
                    <label htmlFor="name" className="label">
                      <span className="label-text">Nombre *</span>
                    </label>
                    <input
                      id="name"
                      type="text"
                      name="name"
                      placeholder="Nombre"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      minLength={2}
                      autoComplete="given-name"
                      disabled={loading}
                      className="input input-bordered w-full"
                    />
                  </div>

                  <div className="form-control">
                    <label htmlFor="apellido" className="label">
                      <span className="label-text">Apellido *</span>
                    </label>
                    <input
                      id="apellido"
                      type="text"
                      name="apellido"
                      placeholder="Apellido"
                      value={formData.apellido}
                      onChange={handleChange}
                      required
                      minLength={2}
                      autoComplete="family-name"
                      disabled={loading}
                      className="input input-bordered w-full"
                    />
                  </div>
                </div>

                {/* fila 2 */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="form-control">
                    <label htmlFor="number" className="label">
                      <span className="label-text">Número de contacto *</span>
                    </label>
                    <input
                      id="number"
                      type="tel"
                      name="number"
                      placeholder="Ej: +54 9 11 1234-5678"
                      value={formData.number}
                      onChange={handleChange}
                      required
                      autoComplete="tel"
                      inputMode="tel"
                      pattern="^[0-9+\-() ]{6,}$"
                      disabled={loading}
                      className="input input-bordered w-full"
                    />
                  </div>

                  <div className="form-control">
                    <label htmlFor="sendto" className="label">
                      <span className="label-text">Mail *</span>
                    </label>
                    <input
                      id="sendto"
                      type="email"
                      name="sendto"
                      placeholder="tu@correo.com"
                      value={formData.sendto}
                      onChange={handleChange}
                      required
                      autoComplete="email"
                      disabled={loading}
                      className="input input-bordered w-full"
                    />
                  </div>
                </div>

                {/* fila 3 */}
                <div className="form-control">
                  <label htmlFor="hotel" className="label">
                    <span className="label-text">Hotel *</span>
                  </label>
                  <select
                    id="hotel"
                    name="hotel"
                    value={formData.hotel}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    className="select select-bordered w-full"
                  >
                    {hotelOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* fila 4 */}
                <div className="form-control">
                  <label htmlFor="message" className="label">
                    <span className="label-text">Mensaje *</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Contanos el número de reserva, fechas y cualquier detalle relevante."
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    required
                    minLength={10}
                    disabled={loading}
                    className="textarea textarea-bordered w-full"
                  />
                  <label className="label">
                    <span className="label-text-alt">Recordá: hasta 10 días corridos y 24 h antes del check-in.</span>
                    <span className="label-text-alt">{formData.message.length}/1000</span>
                  </label>
                </div>

                {/* botón */}
                <button
                  type="submit"
                  disabled={loading}
                  className={`btn w-full font-semibold text-white border-none
                    ${loading ? 'bg-red-400 pointer-events-none' : 'bg-red-500 hover:bg-red-600'}`}
                  aria-busy={loading}
                  aria-disabled={loading}
                >
                  {loading && <span className="loading loading-spinner loading-sm"></span>}
                  {loading ? 'Enviando…' : 'Enviar'}
                </button>

                <p className="text-[11px] text-center opacity-70">
                  Al enviar aceptás el tratamiento de tus datos según nuestra política de privacidad.
                </p>

                {/* Mantener mensaje accesible para lectores de pantalla */}
                <span className="sr-only" aria-live="polite">{responseMessage}</span>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactForm;